export type DisplayMode = 'comfort' | 'compact' | 'easy-reader';
export type ColorMode = 'light' | 'dark';

export interface ThemeConfig {
  colors: {
    background: string;
    backgroundSecondary: string;
    backgroundTertiary: string;
    foreground: string;
    foregroundSecondary: string;
    foregroundTertiary: string;
    primary: string;
    primaryHover: string;
    secondary: string;
    border: string;
    borderSecondary: string;
    card: string;
    cardHover: string;
    success: string;
    successBackground: string;
    error: string;
    errorBackground: string;
    warning: string;
    warningBackground: string;
    muted: string;
    backgroundHover: string;
    borderHover: string;
    inputBackground: string;
    inputBorder: string;
    inputFocus: string;
    inputText: string;
    overlayBackground: string;
    overlayOpacity: string;
    backgroundGradient: string;
    primaryGradient: string;
    primaryGradientLight: string;
    primaryGradientMedium: string;
    infoBackground: string;
    infoText: string;
    infoBorder: string;
    purpleBackground: string;
    purpleText: string;
    purpleBorder: string;
    amberBackground: string;
    amberText: string;
    amberBorder: string;
    loadingBackground: string;
    loadingForeground: string;
    skeletonBackground: string;
  };
  typography: {
    textXs: string;
    textSm: string;
    textBase: string;
    textLg: string;
    textXl: string;
    text2xl: string;
    text3xl: string;
    heading: {
      h1: { size: string; weight: string; lineHeight: string };
      h2: { size: string; weight: string; lineHeight: string };
      h3: { size: string; weight: string; lineHeight: string };
      h4: { size: string; weight: string; lineHeight: string };
      h5: { size: string; weight: string; lineHeight: string };
      h6: { size: string; weight: string; lineHeight: string };
    };
    sectionHeading: { size: string; weight: string; spacing: string };
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  form: {
    fieldGap: string;
    sectionGap: string;
    labelMargin: string;
    inputPadding: string;
  };
}

const FONT_SIZES: Record<DisplayMode, ThemeConfig['typography']> = {
  comfort: {
    textXs: 'text-[13px]',
    textSm: 'text-[15px]',
    textBase: 'text-[17px]',
    textLg: 'text-[19px]',
    textXl: 'text-[21px]',
    text2xl: 'text-[25px]',
    text3xl: 'text-[31px]',
    heading: {
      h1: { size: 'text-[31px]', weight: 'font-bold', lineHeight: 'leading-tight' },
      h2: { size: 'text-[25px]', weight: 'font-bold', lineHeight: 'leading-tight' },
      h3: { size: 'text-[21px]', weight: 'font-semibold', lineHeight: 'leading-snug' },
      h4: { size: 'text-[19px]', weight: 'font-semibold', lineHeight: 'leading-snug' },
      h5: { size: 'text-[17px]', weight: 'font-semibold', lineHeight: 'leading-normal' },
      h6: { size: 'text-[15px]', weight: 'font-semibold', lineHeight: 'leading-normal' },
    },
    sectionHeading: { size: 'text-[21px]', weight: 'font-semibold', spacing: 'mb-4' },
  },
  compact: {
    textXs: 'text-[11px]',
    textSm: 'text-[13px]',
    textBase: 'text-[15px]',
    textLg: 'text-[17px]',
    textXl: 'text-[19px]',
    text2xl: 'text-[23px]',
    text3xl: 'text-[29px]',
    heading: {
      h1: { size: 'text-[29px]', weight: 'font-bold', lineHeight: 'leading-tight' },
      h2: { size: 'text-[23px]', weight: 'font-bold', lineHeight: 'leading-tight' },
      h3: { size: 'text-[19px]', weight: 'font-semibold', lineHeight: 'leading-snug' },
      h4: { size: 'text-[17px]', weight: 'font-semibold', lineHeight: 'leading-snug' },
      h5: { size: 'text-[15px]', weight: 'font-semibold', lineHeight: 'leading-normal' },
      h6: { size: 'text-[13px]', weight: 'font-semibold', lineHeight: 'leading-normal' },
    },
    sectionHeading: { size: 'text-[19px]', weight: 'font-semibold', spacing: 'mb-3' },
  },
  'easy-reader': {
    textXs: 'text-[14px]',
    textSm: 'text-[16px]',
    textBase: 'text-[18px]',
    textLg: 'text-[20px]',
    textXl: 'text-[22px]',
    text2xl: 'text-[26px]',
    text3xl: 'text-[32px]',
    heading: {
      h1: { size: 'text-[32px]', weight: 'font-bold', lineHeight: 'leading-tight' },
      h2: { size: 'text-[26px]', weight: 'font-bold', lineHeight: 'leading-tight' },
      h3: { size: 'text-[22px]', weight: 'font-semibold', lineHeight: 'leading-snug' },
      h4: { size: 'text-[20px]', weight: 'font-semibold', lineHeight: 'leading-snug' },
      h5: { size: 'text-[18px]', weight: 'font-semibold', lineHeight: 'leading-normal' },
      h6: { size: 'text-[16px]', weight: 'font-semibold', lineHeight: 'leading-normal' },
    },
    sectionHeading: { size: 'text-[22px]', weight: 'font-semibold', spacing: 'mb-5' },
  },
};

const SPACING: Record<DisplayMode, ThemeConfig['spacing']> = {
  comfort: {
    xs: 'p-1 gap-1',
    sm: 'p-2 gap-2',
    md: 'p-4 gap-4',
    lg: 'p-6 gap-6',
    xl: 'p-8 gap-8',
    '2xl': 'p-10 gap-10',
  },
  compact: {
    xs: 'p-0.5 gap-0.5',
    sm: 'p-1.5 gap-1.5',
    md: 'p-3 gap-3',
    lg: 'p-4 gap-4',
    xl: 'p-6 gap-6',
    '2xl': 'p-8 gap-8',
  },
  'easy-reader': {
    xs: 'p-1.5 gap-1.5',
    sm: 'p-2.5 gap-2.5',
    md: 'p-5 gap-5',
    lg: 'p-7 gap-7',
    xl: 'p-9 gap-9',
    '2xl': 'p-11 gap-11',
  },
};

const FORM_SPACING: Record<DisplayMode, ThemeConfig['form']> = {
  comfort: { fieldGap: 'gap-8', sectionGap: 'gap-10', labelMargin: 'mb-3', inputPadding: 'px-4 py-3' },
  compact: { fieldGap: 'gap-6', sectionGap: 'gap-8', labelMargin: 'mb-2', inputPadding: 'px-3 py-2' },
  'easy-reader': { fieldGap: 'gap-9', sectionGap: 'gap-11', labelMargin: 'mb-4', inputPadding: 'px-5 py-4' },
};

const LIGHT_COLORS: ThemeConfig['colors'] = {
  background: 'bg-gradient-to-br from-slate-50 via-white to-slate-50',
  backgroundSecondary: 'bg-white',
  backgroundTertiary: 'bg-slate-50',
  foreground: 'text-slate-900',
  foregroundSecondary: 'text-slate-700',
  foregroundTertiary: 'text-slate-600',
  primary: 'text-primary',
  primaryHover: 'text-primary-hover',
  secondary: 'text-slate-500',
  border: 'border-slate-200',
  borderSecondary: 'border-slate-300',
  card: 'bg-white',
  cardHover: 'bg-slate-50',
  success: 'text-emerald-600',
  successBackground: 'bg-emerald-100',
  error: 'text-red-600',
  errorBackground: 'bg-red-100',
  warning: 'text-yellow-600',
  warningBackground: 'bg-yellow-100',
  muted: 'text-slate-400',
  backgroundHover: 'bg-slate-100',
  borderHover: 'border-slate-300',
  inputBackground: 'bg-white',
  inputBorder: 'border-slate-300',
  inputFocus: 'ring-primary border-primary',
  inputText: 'text-slate-900',
  overlayBackground: 'bg-black bg-opacity-50',
  overlayOpacity: 'bg-opacity-50',
  backgroundGradient: 'bg-gradient-to-br from-slate-50 via-white to-slate-50',
  primaryGradient: 'bg-gradient-to-r from-primary to-primary-light',
  primaryGradientLight: 'bg-gradient-to-br from-primary/10 to-primary-light/5',
  primaryGradientMedium: 'bg-gradient-to-br from-primary/20 to-primary-light/10',
  infoBackground: 'bg-blue-50',
  infoText: 'text-blue-700',
  infoBorder: 'border-blue-200',
  purpleBackground: 'bg-purple-50',
  purpleText: 'text-purple-700',
  purpleBorder: 'border-purple-200',
  amberBackground: 'bg-amber-50',
  amberText: 'text-amber-700',
  amberBorder: 'border-amber-200',
  loadingBackground: 'bg-slate-200',
  loadingForeground: 'text-slate-600',
  skeletonBackground: 'bg-slate-200',
};

const DARK_COLORS: ThemeConfig['colors'] = {
  background: 'bg-slate-950',
  backgroundSecondary: 'bg-slate-900',
  backgroundTertiary: 'bg-slate-800',
  foreground: 'text-slate-100',
  foregroundSecondary: 'text-slate-200',
  foregroundTertiary: 'text-slate-300',
  primary: 'text-primary',
  primaryHover: 'text-primary-hover',
  secondary: 'text-slate-400',
  border: 'border-slate-700',
  borderSecondary: 'border-slate-600',
  card: 'bg-slate-800',
  cardHover: 'bg-slate-700',
  success: 'text-emerald-400',
  successBackground: 'bg-emerald-900/30',
  error: 'text-red-400',
  errorBackground: 'bg-red-900/30',
  warning: 'text-yellow-400',
  warningBackground: 'bg-yellow-900/30',
  muted: 'text-slate-400',
  backgroundHover: 'bg-slate-800',
  borderHover: 'border-slate-600',
  inputBackground: 'bg-slate-800',
  inputBorder: 'border-slate-600',
  inputFocus: 'ring-slate-400 border-slate-400',
  inputText: 'text-slate-100',
  overlayBackground: 'bg-black bg-opacity-60',
  overlayOpacity: 'bg-opacity-60',
  backgroundGradient: 'bg-slate-950',
  primaryGradient: 'bg-gradient-to-r from-primary to-primary-light',
  primaryGradientLight: 'bg-gradient-to-br from-primary/20 to-primary-light/10',
  primaryGradientMedium: 'bg-gradient-to-br from-primary/30 to-primary-light/20',
  infoBackground: 'bg-blue-900/30',
  infoText: 'text-blue-400',
  infoBorder: 'border-blue-800',
  purpleBackground: 'bg-purple-900/30',
  purpleText: 'text-purple-400',
  purpleBorder: 'border-purple-800',
  amberBackground: 'bg-amber-900/30',
  amberText: 'text-amber-400',
  amberBorder: 'border-amber-800',
  loadingBackground: 'bg-slate-700',
  loadingForeground: 'text-slate-300',
  skeletonBackground: 'bg-slate-700',
};

export const getThemeConfig = (displayMode: DisplayMode, colorMode: ColorMode): ThemeConfig => ({
  colors: colorMode === 'dark' ? DARK_COLORS : LIGHT_COLORS,
  typography: FONT_SIZES[displayMode],
  spacing: SPACING[displayMode],
  borderRadius: { sm: 'rounded-lg', md: 'rounded-xl', lg: 'rounded-xl', xl: 'rounded-2xl' },
  form: FORM_SPACING[displayMode],
});

export const getTextSize = (
  baseSize: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl',
  displayMode: DisplayMode
): string => {
  const key = `text${baseSize.charAt(0).toUpperCase()}${baseSize.slice(1)}` as keyof ThemeConfig['typography'];
  return FONT_SIZES[displayMode][key] as string;
};

export const getSpacing = (
  baseSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl',
  displayMode: DisplayMode,
  type: 'padding' | 'gap' | 'margin' = 'padding'
): string => {
  const combined = SPACING[displayMode][baseSize];
  const parts = combined.split(' ');
  if (type === 'padding') return parts[0];
  if (type === 'gap') return parts[1] || parts[0];
  return parts[0].replace('p-', 'm-');
};

export const getBackgroundColor = (
  variant: 'primary' | 'secondary' | 'tertiary' | 'card',
  colorMode: ColorMode
): string => {
  const colors = colorMode === 'dark' ? DARK_COLORS : LIGHT_COLORS;
  switch (variant) {
    case 'primary':
      return colors.background;
    case 'secondary':
      return colors.backgroundSecondary;
    case 'tertiary':
      return colors.backgroundTertiary;
    case 'card':
      return colors.card;
    default:
      return colors.background;
  }
};

export const getTextColor = (variant: 'primary' | 'secondary' | 'tertiary' | 'muted', colorMode: ColorMode): string => {
  const colors = colorMode === 'dark' ? DARK_COLORS : LIGHT_COLORS;
  switch (variant) {
    case 'primary':
      return colors.foreground;
    case 'secondary':
      return colors.foregroundSecondary;
    case 'tertiary':
      return colors.foregroundTertiary;
    case 'muted':
      return colors.muted;
    default:
      return colors.foreground;
  }
};

export const getBorderColor = (variant: 'primary' | 'secondary', colorMode: ColorMode): string => {
  const colors = colorMode === 'dark' ? DARK_COLORS : LIGHT_COLORS;
  return variant === 'primary' ? colors.border : colors.borderSecondary;
};
